import React, { useEffect, useState } from 'react';
import { PhoneOutgoing, AlertTriangle, Loader2 } from 'lucide-react';
import axiosInstance from 'api/axios.config';
import { API_ENDPOINTS } from 'api/endpoints';
import { useAuth } from 'hooks/useAuth';
import { listFrom } from 'api/unwrap';

/**
 * Place an outbound call to a customer.
 *
 * The organisation's own number is presented, and the call bills to the
 * organisation — so the page is deliberately quiet about what it can do until it
 * knows the call would be permitted. Everything it needs is checked server-side
 * anyway; the point of doing it here too is that a refusal after the customer's
 * phone rings is worse than a button that was never enabled.
 */
const INPUT = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ' +
  'bg-white dark:bg-gray-700 text-gray-900 dark:text-white';


type Num = { id?: number; e164?: string; business_id?: string; status?: string };
type Seat = { extension?: string; displayName?: string; display_name?: string };
type Group = { id?: string; extension?: string; name?: string };

const OutboundCall: React.FC = () => {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id;

  const [numbers, setNumbers] = useState<Num[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingNumbers, setLoadingNumbers] = useState(true);
  // Where the customer is connected once they answer. Required — see canPlace.
  const [target, setTarget] = useState('');
  const [seats, setSeats] = useState<Seat[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  // AI agents this call can be handed to.
  //
  // Not a dialplan extension: nothing in the dialplan reaches an agent. The call is
  // originated carrying the agent's uuid and socket in its custom data — the same
  // primitive the campaign dialler uses, without a campaign wrapped round it, so the
  // call goes out NOW rather than waiting on a campaign's pacing.
  const [agents, setAgents] = useState<any[]>([]);
  const [agentId, setAgentId] = useState('');

  useEffect(() => {
    (async () => {
      setLoadingNumbers(true);
      try {
        // Org-scoped, and read from the same query that authorises the call —
        // the previous endpoint was the platform-wide inventory, which ignored
        // organizationId and offered numbers this org does not hold.
        const res = await axiosInstance.get(API_ENDPOINTS.OUTBOUND_NUMBERS);
        const list: Num[] = listFrom(res.data, 'numbers');
        setNumbers(list);
        // One number is not a choice — pick it so the common case is two fields,
        // not three.
        if (list.length === 1 && list[0].e164) setFrom(list[0].e164);
      } catch {
        // Not fatal: the caller ID can still be typed, and the server checks it
        // against what the organisation holds regardless of what this list shows.
        setNumbers([]);
      } finally {
        setLoadingNumbers(false);
      }
    })();
  }, [orgId]);

  // The things a call can be connected to: this organisation's own seats and
  // ring groups. Both are org-scoped server-side, so the list cannot show
  // another tenant's extensions even if the request asked for them.
  useEffect(() => {
    (async () => {
      try {
        const [d, g] = await Promise.all([
          axiosInstance.get(API_ENDPOINTS.DIRECTORY, { params: { organizationId: orgId } }),
          axiosInstance.get(API_ENDPOINTS.GROUPS, { params: { organizationId: orgId } }),
        ]);
        setSeats(listFrom(d.data, 'entries'));
        setGroups(listFrom(g.data, 'groups'));
      } catch {
        // Seats and ring groups are the EXTENSION route only. An organisation with
        // no softphone has none of either, and that must not stop the AI route from
        // loading — which is why the agents fetch is its own effect below and not
        // nested in this try, where it silently never ran.
        setSeats([]);
        setGroups([]);
      }
    })();
  }, [orgId]);

  // The agents this call can be handed to.
  //
  // Loaded INDEPENDENTLY of seats and ring groups. Nested inside their request it
  // never ran for exactly the organisations this feature exists for: no softphone
  // means the directory call fails, and the AI option then never appeared at all.
  useEffect(() => {
    (async () => {
      try {
        const a = await axiosInstance.get(API_ENDPOINTS.AI_AGENTS, { params: { organizationId: orgId } });
        const rows: any[] = (a.data?.data || a.data || []) as any[];
        setAgents(Array.isArray(rows) ? rows.filter((x: any) => x && x.id) : []);
      } catch {
        // No agents is an ordinary answer — the AI option simply does not appear.
        setAgents([]);
      }
    })();
  }, [orgId]);

  const place = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      // Two genuinely different paths, not two values of one field.
      //
      // A seat or ring group is a dialplan extension: the switch originates and puts
      // the answered leg into this organisation's context.
      //
      // An AI agent is not reachable that way — no extension points at one. The call
      // is queued into a voice campaign instead, which is how every AI call is placed
      // today, and the campaign's own dialer originates it.
      const res = agentId
        ? await axiosInstance.post('/v1/telephony/outbound/ai-call',
            { from: from || undefined, to, agentId }, { params: { organizationId: orgId } })
        : await axiosInstance.post(API_ENDPOINTS.OUTBOUND_CALL, { from, to, target: target || undefined });
      setResult(res.data?.data ?? res.data);
    } catch (e: any) {
      // The API's own words. "outbound calling is not enabled for this
      // organization" and "this organization does not hold +91…" send someone to
      // completely different places, and a generic failure sends them nowhere.
      setError(e?.response?.data?.message || e?.message || 'The call could not be placed');
    } finally {
      setBusy(false);
    }
  };

  // `target` is REQUIRED, not a nicety. Without it the answered leg goes to the
  // switch's inbound context keyed on the number we just dialled — the customer's
  // own mobile, which is by definition not a DID we own. Asterisk finds no route
  // and hangs up the instant they answer (Q.850 cause 3, observed 2026-08-14:
  // rang 6.5s, ANSWER, then Hangup 2ms later). There is no correct default here,
  // so the choice is asked for rather than invented.
  // A target is required for the EXTENSION route and meaningless for the AI one:
  // the campaign supplies both the agent and the caller ID, so there is nothing to
  // connect the answered leg to. Requiring it anyway leaves the button dead with no
  // explanation once an agent is chosen.
  // A caller ID is required on BOTH routes — it is the number the call presents, and
  // the server refuses without one whether an agent or a person answers. Only the
  // TARGET is route-specific: an AI call has an agent instead of an extension.
  //
  // Letting the button enable without a caller ID would send a request the server has
  // already decided to refuse, which reads as a broken feature rather than a missing
  // field.
  const canPlace = !!to.trim() && !!from.trim() && !busy
    && (agentId ? true : !!target.trim());

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Place a call</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Rings the customer first and presents one of your organisation's numbers. Hand it to an
        AI agent, or connect it to a seat or ring group — an AI call needs no softphone and no
        seat, so this works for organisations that only run outbound agents. The call is
        billed to your organisation.
      </p>

      <div className="mt-6 space-y-4 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <label className="block">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Present this number</span>
          {loadingNumbers ? (
            <div className="mt-1 text-sm text-gray-500">loading your numbers…</div>
          ) : numbers.length ? (
            <select value={from} onChange={e => setFrom(e.target.value)} className={`mt-1 ${INPUT}`}>
              <option value="">Choose a number…</option>
              {numbers.filter(n => n.e164).map(n => (
                <option key={n.id ?? n.e164} value={n.e164}>{n.e164}</option>
              ))}
            </select>
          ) : (
            <>
              <input value={from} onChange={e => setFrom(e.target.value)} className={`mt-1 ${INPUT}`}
                     placeholder="+912265197350" />
              <span className="mt-1 block text-xs text-gray-500">
                No numbers are listed for this organisation. You can type one, but the call is
                refused unless the organisation actually holds it.
              </span>
            </>
          )}
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Call this customer</span>
          <input value={to} onChange={e => setTo(e.target.value)} className={`mt-1 ${INPUT}`}
                 placeholder="+919876543210" inputMode="tel" />
        </label>

        {/* An AI agent is a different ROUTE, not another target: the call is queued

            into a voice campaign whose dialer places it, because no dialplan extension

            reaches an agent. Offering it inside the extension list would look identical

            and produce a call that cuts the instant the customer answers. */}

        {agents.length > 0 && (

          <label className="block">

            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Or hand it to an AI agent</span>

            <select value={agentId} onChange={e => { setAgentId(e.target.value); if (e.target.value) setTarget(""); }} className={`mt-1 ${INPUT}`}>
                <option value="">No — connect to a person below</option>
                {agents.map((a: any) => (
                  <option key={a.id} value={a.id}>{a.name || `Agent ${a.id}`}</option>
                ))}
              </select>

            <span className="mt-1 block text-xs text-gray-500">

              The call is placed now and the agent answers it. An agent that has never been
              pushed to the voice runtime is refused rather than dialled — a call with no
              socket rings a real person and is answered by nothing.

            </span>

          </label>

        )}


        <label className="block">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Connect them to
          </span>
          <select value={target} disabled={!!agentId} onChange={e => setTarget(e.target.value)} className={`mt-1 ${INPUT} ${agentId ? "opacity-50" : ""}`}>
            <option value="">Choose where the call lands…</option>
            {groups.filter(g => g.extension).length > 0 && (
              <optgroup label="Ring groups">
                {groups.filter(g => g.extension).map(g => (
                  <option key={`g-${g.extension}`} value={g.extension}>
                    {g.name} ({g.extension})
                  </option>
                ))}
              </optgroup>
            )}
            {seats.filter(s => s.extension).length > 0 && (
              <optgroup label="Agents">
                {seats.filter(s => s.extension).map(s => (
                  <option key={`s-${s.extension}`} value={s.extension}>
                    {s.displayName || s.display_name || 'Seat'} ({s.extension})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <span className="mt-1 block text-xs text-gray-500">
            The customer hears ringing first; this is where they land once they answer.
            Required — an answered call with nowhere to go is cut off the moment it
            connects.
          </span>
        </label>

        <button
          onClick={place}
          disabled={!canPlace}
          className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white
                     hover:bg-green-700 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneOutgoing className="h-4 w-4" />}
          {busy ? 'Placing…' : 'Place call'}
        </button>
      </div>

      {error && (
        <div className="mt-4 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3
                        text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="mt-4 rounded-lg border border-green-300 bg-green-50 p-3 text-sm
                        text-green-900 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200">
          {/* "Ringing", never "connected": the API returns the moment the carrier
              accepts the call, and whether anyone answers arrives later in the CDR. */}
          <div className="font-medium">Ringing {result.to}</div>
          <div className="mt-1 text-xs opacity-80">
            presenting {result.from} · trunk {result.trunk}
            {result.connectTo ? <> · connecting to {result.connectTo}</> : null}
            {result.callId ? <> · call {result.callId}</> : null}
          </div>
          {result.note && <div className="mt-2 text-xs opacity-80">{result.note}</div>}
        </div>
      )}
    </div>
  );
};

export default OutboundCall;
